import { Injectable } from '@angular/core';
import { Animal } from '../../animal';
import { AnimalEvent } from '../animal-event';
import { Herdout } from './herdout';
import { HerdoutSummary } from './herdout-summary';
import {
    Filter,
    FilterOp,
    NetworkStatusService,
    SortMode,
    SortOrder
} from 'libs/arch/src/lib/repositories';
import {
    BaseIndexedDbRepository,
    SynchronizationService
} from 'libs/arch/src/lib/repositories/indexed-db';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { forkJoin, map, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApiService } from '@homeleite/arch';

@Injectable({ providedIn: 'root' })
export class HerdoutRepository extends BaseIndexedDbRepository<HerdoutSummary, Herdout> {
    protected override readonly storeName: string = 'herdouts';

    constructor(
        dbService: NgxIndexedDBService,
        synchronizationService: SynchronizationService,
        private networkStatus: NetworkStatusService,
        private apiService: ApiService
    ) {
        super(dbService, synchronizationService);

        this.registerAnimalReferences('animalId');
    }

    protected override mapToEntity(dbItem: Herdout): Herdout {
        return Herdout.fromJson(dbItem);
    }

    protected override mapToSummary(item: Herdout): HerdoutSummary {
        return new HerdoutSummary(item.id, item.animal);
    }

    protected override getSearchableFields(): string[] {
        return ['animal.name', 'animal.register', 'date', 'type'];
    }

    protected override getResourcePath(): string {
        return 'herd-outs';
    }

    public override insert(herdout: Herdout): Observable<Herdout> {
        return super.insert(herdout).pipe(
            tap((savedHerdout) => {
                const transformedEvent: AnimalEvent = {
                    id: savedHerdout.id,
                    date: savedHerdout.date,
                    animal: savedHerdout.animal,
                    type: 'HERD_OUT',
                    created: savedHerdout.created
                };

                this.dbService.add('animalEvents', transformedEvent).subscribe({
                    error: (err) => {
                        console.error(
                            'Falha ao agregar evento de Saída de Rebanho em animalEvents:',
                            err
                        );
                    }
                });
            })
        );
    }

    public findByAnimal(animal: Animal): Observable<Herdout[]> {
        const animalId: string | undefined = animal?.id;
        if (!animalId) {
            return of([]);
        }

        const getFromDb = () => {
            return this.executeIndexedDbQuery({
                filters: [new Filter('animal.id', FilterOp.EQ, animalId)],
                sortOrders: [
                    new SortOrder('date', SortMode.DSC),
                    new SortOrder('created', SortMode.DSC)
                ]
            });
        };

        if (!this.networkStatus.isOnline()) {
            return getFromDb();
        }

        return this.fetchHerdoutsBy(animalId)
            .pipe(catchError(() => getFromDb()));
    }

    private fetchHerdoutsBy(animalId: string): Observable<Herdout[]> {
        return this.apiService.root
            .path(this.getResourcePath())
            .path('animal')
            .path(animalId)
            .getJson<any[]>()
            .pipe(
                map((response) =>
                    Array.isArray(response) ? response.map((item) => Herdout.fromJson(item)) : []
                ),
                tap((herdouts) => {
                    if (!herdouts?.length) return;

                    const integrationHerdouts = herdouts.filter((herdout) => {
                        const { auditData } = herdout;
                        return (
                            auditData?.createdByIntegration || (auditData?.createdByApp && auditData?.modifiedByIntegration)
                        );
                    });

                    if (integrationHerdouts.length > 0) {
                        this.saveLocally(integrationHerdouts).subscribe({
                            error: (err) => console.error('Failed to save herdouts in batch:', err)
                        });
                    }
                })
            );
    }
}
